/* Shared site logic - runs on every public page.
 * Ported from designer's assets/js/site.js: footer year, mobile nav toggle,
 * FAQ accordion, multi-step Wizard helper. Auto-binds on import.
 */
import { setButtonBusy } from './lib/busy';

function bindFooterYear(): void {
  const el = document.getElementById('year');
  if (el) el.textContent = String(new Date().getFullYear());
}

function bindMobileNav(): void {
  const btn = document.getElementById('nav-toggle');
  const menu = document.getElementById('mobile-menu');
  if (!btn || !menu) return;

  btn.addEventListener('click', () => {
    const isOpen = menu.classList.toggle('is-open');
    btn.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
  });
  menu.querySelectorAll('a').forEach((a) => {
    a.addEventListener('click', () => {
      menu.classList.remove('is-open');
      btn.setAttribute('aria-expanded', 'false');
    });
  });
}

function bindFaqAccordion(): void {
  document.querySelectorAll<HTMLElement>('.faq__q').forEach((q) => {
    q.addEventListener('click', () => {
      const item = q.closest('.faq__item');
      item?.classList.toggle('is-open');
    });
  });
}

/** Bootstraps the elements site.js used to bind. Idempotent. */
export function initSite(): void {
  bindFooterYear();
  bindMobileNav();
  bindFaqAccordion();
}

// Multi-step wizard helper (used on request.html). Translated from designer's
// window.SkWizard global to a named export the page TS modules import.

export interface WizardOptions {
  /** Called when the user clicks the final "Submit" button on the last panel.
   *  Return a Promise to defer navigation; throw to keep the user on the panel. */
  onSubmit?: () => Promise<void> | void;
}

export function initWizard(rootSelector: string, options: WizardOptions = {}): void {
  const root = document.querySelector<HTMLElement>(rootSelector);
  if (!root) return;

  const panels = Array.from(root.querySelectorAll<HTMLElement>('.wizard-step-panel'));
  const stepItems = Array.from(root.querySelectorAll<HTMLElement>('.wizard__step'));
  const prevBtn = root.querySelector<HTMLElement>('[data-wizard-prev]');
  const nextBtn = root.querySelector<HTMLElement>('[data-wizard-next]');
  const submitBtn = root.querySelector<HTMLElement>('[data-wizard-submit]');
  let current = 0;

  function show(idx: number): void {
    idx = Math.max(0, Math.min(panels.length - 1, idx));
    current = idx;
    panels.forEach((p, i) => p.classList.toggle('is-active', i === idx));
    stepItems.forEach((s, i) => {
      s.classList.toggle('is-current', i === idx);
      s.classList.toggle('is-done', i < idx);
    });
    if (prevBtn) prevBtn.style.visibility = idx === 0 ? 'hidden' : 'visible';
    if (nextBtn) nextBtn.style.display = idx === panels.length - 1 ? 'none' : 'inline-flex';
    if (submitBtn) submitBtn.style.display = idx === panels.length - 1 ? 'inline-flex' : 'none';

    if (idx > 0) root!.querySelector<HTMLElement>('.wizard__panel')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function validateCurrent(): boolean {
    const panel = panels[current];
    if (!panel) return true;
    let ok = true;
    panel.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>(
      'input[required], select[required], textarea[required]'
    ).forEach((inp) => {
      const value = inp.value;
      const emailBad = inp instanceof HTMLInputElement && inp.type === 'email' && !/.+@.+\..+/.test(value);
      // `min`/`max` violations, e.g. a date before today on the request form.
      const outOfRange = inp instanceof HTMLInputElement && (inp.validity.rangeUnderflow || inp.validity.rangeOverflow);
      if (!value || emailBad || outOfRange) {
        inp.style.borderColor = '#b6433a';
        if (ok && outOfRange) inp.reportValidity();
        ok = false;
      } else {
        inp.style.borderColor = '';
      }
    });
    return ok;
  }

  nextBtn?.addEventListener('click', () => { if (validateCurrent()) show(current + 1); });
  prevBtn?.addEventListener('click', () => show(current - 1));

  submitBtn?.addEventListener('click', async (e) => {
    e.preventDefault();
    if (!validateCurrent()) return;
    if (options.onSubmit) {
      const busyBtn = submitBtn instanceof HTMLButtonElement ? submitBtn : null;
      setButtonBusy(busyBtn, true);
      try {
        await options.onSubmit();
      } catch (err) {
        console.error('[wizard] submit failed', err);
        return;
      } finally {
        setButtonBusy(busyBtn, false);
      }
    } else {
      // Default behaviour matches designer's site.js: redirect to the
      // post-submit page.
      const pointParam = new URLSearchParams(window.location.search).get('point') ?? '';
      window.location.href = pointParam
        ? `request-submitted.html?point=${encodeURIComponent(pointParam)}`
        : 'request-submitted.html';
    }
  });

  stepItems.forEach((s, i) => {
    s.style.cursor = 'pointer';
    s.addEventListener('click', () => { if (i < current) show(i); });
  });

  show(0);
}

initSite();
